import React, { useEffect, useMemo, useRef, useState } from "react";
import "../index.css";

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const smoothstep = (t) => t * t * (3 - 2 * t);
const lerp = (a, b, t) => a + (b - a) * t;

function mixRGBA(a, b, t) {
  const r = Math.round(lerp(a.r, b.r, t));
  const g = Math.round(lerp(a.g, b.g, t));
  const bb = Math.round(lerp(a.b, b.b, t));
  const alpha = lerp(a.a, b.a, t);
  return `rgba(${r}, ${g}, ${bb}, ${alpha})`;
}

function mixRGBString(a, b, t, alpha = 1) {
  const r = Math.round(lerp(a[0], b[0], t));
  const g = Math.round(lerp(a[1], b[1], t));
  const bb = Math.round(lerp(a[2], b[2], t));
  return `rgba(${r}, ${g}, ${bb}, ${alpha})`;
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpoint);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [breakpoint]);

  return isMobile;
}

function useViewportHeight() {
  const [vh, setVh] = useState(() =>
    typeof window !== "undefined"
      ? window.visualViewport?.height || window.innerHeight
      : 0
  );

  useEffect(() => {
    const update = () => {
      const next = window.visualViewport?.height || window.innerHeight || 0;
      setVh(next);
      document.documentElement.style.setProperty("--app-vh", `${next}px`);
    };

    update();

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  return vh;
}

export default function BlackLockAndTextReveal({
  zoneVh = 260,
  finishAt = 0.6,
  from = { r: 252, g: 249, b: 248, a: 1 },
  to = { r: 24, g: 21, b: 20, a: 1 },
  prefix = "",
  words = ["Break", "up", "with", "your", "bank."],
}) {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const viewportH = useViewportHeight();

  const [bgT, setBgT] = useState(0);
  const [revealT, setRevealT] = useState(0);

  // Mobile: shorter section and earlier finish
  const resolvedZoneVh = isMobile ? 165 : zoneVh;
  const resolvedFinishAt = isMobile ? 0.3 : finishAt;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const vh = viewportH || window.innerHeight || 1;
      const zonePx = (resolvedZoneVh / 100) * vh;

      const scrolledPastTop = Math.max(0, -rect.top);
      const raw = clamp01(scrolledPastTop / Math.max(zonePx, 1));

      const fadeRaw = clamp01(raw / Math.max(0.0001, resolvedFinishAt));

      // Faster-feeling rise on mobile
      const eased = isMobile ? Math.pow(fadeRaw, 0.7) : smoothstep(fadeRaw);

      setBgT(eased);
      setRevealT(eased);

      raf = 0;
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("orientationchange", onScroll);
    window.visualViewport?.addEventListener("resize", onScroll);
    window.visualViewport?.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("orientationchange", onScroll);
      window.visualViewport?.removeEventListener("resize", onScroll);
      window.visualViewport?.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [resolvedZoneVh, resolvedFinishAt, viewportH, isMobile]);

  const bg = useMemo(() => mixRGBA(from, to, bgT), [from, to, bgT]);

  const textFlipT = clamp01((bgT - (isMobile ? 0.42 : 0.55)) / (isMobile ? 0.26 : 0.35));
  const baseText = mixRGBString(
    [24, 21, 20],
    [252, 249, 248],
    smoothstep(textFlipT),
    1
  );

  // Mobile: start with more words visible sooner
  const visibleCount = isMobile
    ? Math.min(words.length, Math.max(2, Math.ceil(revealT * (words.length + 1))))
    : revealT <= 0
      ? 0
      : Math.min(words.length, Math.ceil(revealT * words.length));

  const shown = words.slice(0, visibleCount);

  return (
    <section
      ref={ref}
      style={{
        position: "relative",
        height: `${resolvedZoneVh}vh`,
        backgroundColor: bg,
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "var(--app-vh, 100vh)",
          minHeight: "var(--app-vh, 100vh)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "0 20px" : "0 72px",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: "Blackletter, Helvetica, Arial, sans-serif",
            fontWeight: 100,
            letterSpacing: isMobile ? "-0.01em" : "-0.02em",
            lineHeight: isMobile ? 0.98 : 0.95,
            fontSize: isMobile
              ? "clamp(42px, 13vw, 68px)"
              : "clamp(64px, 10vw, 256px)",
            color: baseText,
            textAlign: "center",
            maxWidth: isMobile ? "8ch" : "24ch",
          }}
        >
          {prefix}
          {shown.length ? " " : ""}
          {shown.join(" ")}
        </h1>
      </div>
    </section>
  );
}
